import React, { useState } from 'react'
import Modal from './Modal'
import { KeyIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline'
import { toast } from '../../utils/toast'
import { trackApiKeyCopied } from '../../config/analyticsEvents'

const CreateApiKeyModal = ({ isOpen, onClose, onSubmit, isLoading, createdApiKey }) => {
  const [keyName, setKeyName] = useState('')
  const [step, setStep] = useState(createdApiKey ? 'success' : 'create')
  const [isCopied, setIsCopied] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (keyName.trim()) {
      const result = await onSubmit(keyName.trim())
      if (result && result.apiKey) {
        setStep('success')
      }
    }
  }

  const handleClose = () => {
    // Prevent closing during API key creation
    if (isLoading) return
    
    setKeyName('')
    setStep('create')
    setIsCopied(false)
    onClose()
  }

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      trackApiKeyCopied()
      setIsCopied(true)
      toast.success('API key copied to clipboard!')
      
      // Reset the "Copied" state after 2 seconds
      setTimeout(() => {
        setIsCopied(false)
      }, 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
      toast.error('Failed to copy API key to clipboard')
    }
  }

  if (step === 'success' && createdApiKey) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="API Key Created Successfully" size="md" preventClose={false}>
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <KeyIcon className="h-8 w-8 text-green-600" />
          </div>
          
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            Your API key has been created!
          </h4>
          
          <p className="text-gray-600 mb-6">
            Please copy your API key now. For security reasons, you won't be able to see it again.
          </p>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <code className="text-sm font-mono text-gray-800 break-all">
                {createdApiKey.apiKey}
              </code>
              <button
                onClick={() => copyToClipboard(createdApiKey.apiKey)}
                className="ml-2 p-2 text-gray-500 hover:text-gray-700 transition-colors"
                title="Copy to clipboard"
              >
                <ClipboardDocumentIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <div className="flex justify-center space-x-3">
            <button
              onClick={() => copyToClipboard(createdApiKey.apiKey)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              {isCopied ? 'Copied' : 'Copy API Key'}
            </button>
            <button
              onClick={handleClose}
              className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Done
            </button>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create New API Key" size="md" preventClose={isLoading}>
      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <label htmlFor="keyName" className="block text-sm font-medium text-gray-700 mb-2">
            API Key Name
          </label>
          <input
            type="text"
            id="keyName"
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            placeholder="e.g., Production Key, Development Key"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            required
            disabled={isLoading}
            autoFocus
          />
          <p className="text-xs text-gray-500 mt-1">
            Choose a descriptive name to help you identify this key later.
          </p>
        </div>

        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <h5 className="font-medium text-blue-900 mb-2">Important Security Notes:</h5>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Keep your API key secret and secure</li>
            <li>• Don't share it in public repositories or client-side code</li>
            <li>• You can revoke this key at any time from your dashboard</li>
            <li>• The full key will only be shown once after creation</li>
          </ul>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || !keyName.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Creating...' : 'Create API Key'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default CreateApiKeyModal
